import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
// hooks
import { useShowModal } from 'hooks/useShowModal'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useCurrentForm, useCurrentTable } from 'hooks/risks'
// graphql
import { RISK_CREATE_MUTATION } from 'graphql/risk/mutations'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// types
import { IRisk, IRiskResponse } from 'types/graphql'
// constants
import { Modals } from 'constants/modals'
// components
import { PopoverModal } from 'components/UI'
// icons
import { VerginaSunIcon } from 'components/UI/Icons'
import AddIcon from '@mui/icons-material/Add'
import AddCircle from '@mui/icons-material/AddCircle'
import SearchIcon from '@mui/icons-material/Search'
// themes
import colors from 'themes/colors'
// views
import { ButtonLabel, MenuItem } from './views'
// utils
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

const POPOVER_PROPS = {
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'left' as const
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const
  }
}

interface Props {
  disabled?: boolean
  onAddClick?: (risk?: IRisk) => Promise<void>
  canGenerate: boolean
  newRecordLabel: string
  contextRecordId?: string
}

type RiskCreateResponse = { riskCreate: IRiskResponse }

const AddRowButton: React.FC<Props> = ({ onAddClick, newRecordLabel, disabled, canGenerate, contextRecordId }) => {
  const {
    permissionsFromSecurityGroups: { aiEnableUsage }
  } = useCurrentUser()

  const { fieldLinks } = useCurrentTable()
  const { fieldLink, linked, primaryFieldLink, formRiskTemplate } = useCurrentForm()

  const [createRisk] = useMutation<RiskCreateResponse>(RISK_CREATE_MUTATION)

  const { risk, riskRefetch, refetchLinkedRisksPermissions } = React.useContext(RiskAPIContext)
  const { isRiskTemplate } = React.useContext(RiskTemplateAPIContext)

  const { showModal: showLinkedFormModal } = useShowModal({ modalType: Modals.LINKED_FORM_VIEW_MODAL })
  const { showModal: showAiModal } = useShowModal({ modalType: Modals.AI_LINKED_FORMS_MODAL })

  const label = `Add ${newRecordLabel}`

  const handleOnAddField = async () => {
    if (disabled) {
      return
    }

    try {
      if (linked && fieldLink.primary) {
        const {
          data: {
            riskCreate: { data }
          }
        } = await createRisk({
          variables: {
            input: {
              fieldLinkId: fieldLink.id,
              parentRiskId: risk?.id,
              riskTemplateId: fieldLink.field.entityId,
              isLinkedRiskDefault: isRiskTemplate
            }
          }
        })

        await onAddClick(data)

        await refetchLinkedRisksPermissions?.()
        await riskRefetch?.()
      } else {
        onAddClick()
      }
    } catch (error) {
      console.log('[AddRowButton.handleOnAddField]: ', parseGraphQLErrorMessages(error))
    }
  }

  const onSearchClick = () => {
    if (disabled) {
      return
    }

    showLinkedFormModal({
      fieldLink,
      onAddClick,
      isLinking: true,
      riskViewId: fieldLink.riskViewId
    })
  }

  const generateRows = () => {
    showAiModal({
      contextRecordId,
      addRow: onAddClick,
      fieldLink: primaryFieldLink,
      tableFieldLinks: fieldLinks
    })
  }

  const canLink = fieldLink.linkingSettings?.referenceRecords || fieldLink.linkingSettings?.copyRecords
  const canAdd = !linked || !fieldLink.primary || fieldLink.linkingSettings?.createNewRecords

  const handleClick = (callback: () => void, closePopup: () => void) => {
    callback()
    closePopup()
  }

  if (disabled) {
    return (
      <ButtonLabel disabled data-testid='add-form-row' data-entity-id={fieldLink.field.id}>
        <AddCircle htmlColor={colors.blue500} />
        <span>{label}</span>
      </ButtonLabel>
    )
  }

  return (
    <PopoverModal
      {...POPOVER_PROPS}
      trigger={
        <ButtonLabel data-testid='add-form-row' data-entity-id={fieldLink.field.id}>
          <AddCircle htmlColor={colors.blue500} />
          <span>{label}</span>
        </ButtonLabel>
      }>
      {({ closePopup }) => (
        <>
          <MenuItem
            disabled={!canAdd}
            data-testid='add-form-row-create'
            onClick={() => handleClick(handleOnAddField, closePopup)}>
            <AddIcon />
            {label}
          </MenuItem>
          <MenuItem
            disabled={!canLink}
            data-testid='add-form-row-search'
            onClick={() => handleClick(onSearchClick, closePopup)}>
            <SearchIcon />
            Search or Reference
          </MenuItem>
          {aiEnableUsage && formRiskTemplate?.aiEnabled && (
            <MenuItem disabled={!canGenerate || isRiskTemplate} onClick={() => handleClick(generateRows, closePopup)}>
              <VerginaSunIcon
                sx={{
                  color: 'black',
                }}
              />
              Generate
            </MenuItem>
          )}
        </>
      )}
    </PopoverModal>
  )
}

export default AddRowButton
